/* eslint-disable jsx-a11y/role-supports-aria-props */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import VirtualizedAutocomplete from '../../components/Autocomplete';
import { isFieldLocked } from '../../utils/fieldUtils';
import { abortController } from '../../utils/abortController';
import useSessionExpire from '../../hooks/useSessionExpire';
import axios from 'axios';
import { Divider, Typography } from '@mui/material';
import useTab from '../../hooks/useTab';
import LinkedBuyingPaymentTerms from './PaymentTerms/LinkedBuyingPaymentTerms';
import LinkedSellingPaymentTerms from './PaymentTerms/LinkedSellingPaymentTerms';

const LinkedDealOverridePopup = ({
    open,
    onClose,
    onSubmit,
    linkedDealFormData,
    dealHeaderFormData,
    productData = [],
    isListDataLoading,
    paymentTerms,
    paymentTermTypes = [],
    tenors = [],
    isPeriodLocked,
    lockedFields,
    isWritable,
    selectedBusinessUnit,
    selectedPFI,
    setShowAlert,
    setAlertMessage,
    setAlertType,
    setLinkedDealFormData,
    logOut,
    isFoodsPolymersOrSBLCDeal = false,
    parentBuyingSblcId = null,
    parentSellingSblcId = null,
    parentBuyingPaymentTermTypeId = null,
    parentSellingPaymentTermTypeId = null,
}) => {

    const { selectedLinkedDeal = null } = useTab();
    const { pathname } = useLocation();
    const triggerSessionExpire = useSessionExpire();

    const buyingPTRef = useRef(null);
    const sellingPTRef = useRef(null);
    const hasInitialized = useRef(false);

    const [overriddenData, setOverriddenData] = useState({
        Buyer_Id: null,
        Seller_Id: null,
        Buying_Payment_Terms_Id: null,
        Selling_Payment_Terms_Id: null,
        Buying_Tenor_Id: null,
        Selling_Tenor_Id: null,
        products: []
    });

    const [isSaving, setIsSaving] = useState(false);
    const [expandedProduct, setExpandedProduct] = useState(null);
    const [expandedFormSections, setExpandedFormSections] = useState([5, 6, 7]);
    const [customerCounterParties, setCustomerCounterParties] = useState([]);
    const [supplierCounterParties, setSupplierCounterParties] = useState([]);
    const [isLoadingCounterParties, setIsLoadingCounterParties] = useState(false);
    const prevProductCountRef = useRef(overriddenData?.products?.length || 0);

    // ── Linked deal computed values from overridden product prices ────────────
    const linkedBuyingDealValue = useMemo(() =>
        (overriddenData.products || []).reduce((sum, p) => sum + (parseFloat(p?.Buying_Price) || 0) * (parseFloat(p?.Quantity) || 0), 0),
        [overriddenData.products]
    );

    const linkedSellingDealValue = useMemo(() =>
        (overriddenData.products || []).reduce((sum, p) => sum + (parseFloat(p?.Selling_Price) || 0) * (parseFloat(p?.Quantity) || 0), 0),
        [overriddenData.products]
    );

    const getProductIdField = () => isFoodsPolymersOrSBLCDeal ? 'Deal_Product_Grade_Id' : 'Deal_Product_Id';
    const getProductIdValue = (product) => product?.[getProductIdField()] || null;

    const handleApiError = useCallback((error) => {
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
        const status = error?.response?.status;
        if (status === 403) { triggerSessionExpire(); return; }
        setAlertMessage(
            status === 401 ? "Unauthorized access."
                : status === 429 ? "Request limit exceeded."
                    : "An error occurred. Please try again."
        );
        setAlertType("error");
        setShowAlert(true);
        if (status === 429) setTimeout(logOut, 3000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Fetch counter-parties when popup opens ─────────────────────────────── */
    useEffect(() => {
        const fetchCounterParties = async () => {
            if (!open) return;
            setIsLoadingCounterParties(true);
            try {
                const response = await axios({
                    method: "post",
                    url: "/api/GetCounterPartyList",
                    data: { pathname, Business_Unit_Id: selectedBusinessUnit, Deal_PFI_Id: selectedPFI?.id },
                    signal: abortController.signal
                });
                const { status, data } = response;
                setIsLoadingCounterParties(false);
                if (status === 200) {
                    const { customer_vendors: cvs = [], supplier_vendors: svs = [] } = data || {};
                    setCustomerCounterParties(cvs);
                    setSupplierCounterParties(svs);
                }
            } catch (error) {
                setIsLoadingCounterParties(false);
                if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
                const status = error?.response?.status;
                if (status === 403) { triggerSessionExpire(); return; }
                setAlertMessage(status === 401 ? "Unauthorized access." : status === 429 ? "Request limit exceeded." : "An error occurred while loading counterparties.");
                setAlertType("error");
                setShowAlert(true);
                if (status === 429) setTimeout(logOut, 3000);
            }
        };
        fetchCounterParties();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    /* ── Reset init guard when popup closes ─────────────────────────────────── */
    useEffect(() => {
        if (!open) {
            hasInitialized.current = false;
        }
    }, [open]);

    /* ── Initialise overridden data (once per open) ──────────────────────────── */
    useEffect(() => {
        if (!open || !dealHeaderFormData) return;
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const productIdField = getProductIdField();
        const matchedLinkedIndices = new Set();

        setOverriddenData({
            Buyer_Id: linkedDealFormData?.Buyer_Id || null,
            Seller_Id: linkedDealFormData?.Seller_Id || null,
            Buying_Payment_Terms_Id: linkedDealFormData?.Buying_Payment_Terms_Id || dealHeaderFormData?.Buying_Payment_Terms_Id || null,
            Selling_Payment_Terms_Id: linkedDealFormData?.Selling_Payment_Terms_Id || dealHeaderFormData?.Selling_Payment_Terms_Id || null,
            Buying_Tenor_Id: linkedDealFormData?.Buying_Tenor_Id || dealHeaderFormData?.Buying_Tenor_Id || null,
            Selling_Tenor_Id: linkedDealFormData?.Selling_Tenor_Id || dealHeaderFormData?.Selling_Tenor_Id || null,
            linked_buying_sblc_id: null,
            linked_selling_sblc_id: null,
            products: dealHeaderFormData.products?.map((p, index) => {
                let linkedProduct = null;
                const productId = getProductIdValue(p);

                if (productId) {
                    const foundIndex = linkedDealFormData?.products?.findIndex((lp, lpIndex) => {
                        const linkedProductId = getProductIdValue(lp);
                        return linkedProductId === productId && !matchedLinkedIndices.has(lpIndex);
                    });
                    if (foundIndex !== -1 && foundIndex !== undefined) {
                        linkedProduct = linkedDealFormData.products[foundIndex];
                        matchedLinkedIndices.add(foundIndex);
                    }
                }

                if (!linkedProduct && linkedDealFormData?.products?.[index] && !matchedLinkedIndices.has(index)) {
                    const lpAtSameIndex = linkedDealFormData.products[index];
                    const pProductId = p?.Product_Id?.id || p?.Product_Id;
                    const lpProductId = lpAtSameIndex?.Product_Id?.id || lpAtSameIndex?.Product_Id;
                    if (pProductId === lpProductId) {
                        linkedProduct = lpAtSameIndex;
                        matchedLinkedIndices.add(index);
                    }
                }

                if (!linkedProduct && linkedDealFormData?.products) {
                    const pProductId = p?.Product_Id?.id || p?.Product_Id;
                    const foundIndex = linkedDealFormData.products.findIndex((lp, lpIndex) => {
                        if (matchedLinkedIndices.has(lpIndex)) return false;
                        const lpProductId = lp?.Product_Id?.id || lp?.Product_Id;
                        return pProductId === lpProductId;
                    });
                    if (foundIndex !== -1) {
                        linkedProduct = linkedDealFormData.products[foundIndex];
                        matchedLinkedIndices.add(foundIndex);
                    }
                }

                const productDataItem = {
                    ...p,
                    Buying_Price: linkedProduct?.Buying_Price ?? p?.Buying_Price ?? '',
                    Selling_Price: linkedProduct?.Selling_Price ?? p?.Selling_Price ?? ''
                };

                const linkedProductId = getProductIdValue(linkedProduct);
                productDataItem[productIdField] = linkedProductId || null;

                return productDataItem;
            }) || []
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dealHeaderFormData, isFoodsPolymersOrSBLCDeal]);

    useEffect(() => {
        const currentCount = overriddenData?.products?.length || 0;
        const prevCount = prevProductCountRef.current;
        if (currentCount > prevCount) setExpandedProduct(currentCount - 1);
        prevProductCountRef.current = currentCount;
    }, [overriddenData?.products]);

    useEffect(() => {
        if (open && overriddenData?.products?.length > 0) {
            const productIdField = getProductIdField();
            const firstWithoutId = overriddenData.products.findIndex(p => !p[productIdField]);
            setExpandedProduct(firstWithoutId !== -1 ? firstWithoutId : 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, overriddenData?.products?.length]);

    const handleChange = useCallback((field, value) => {
        setOverriddenData(prev => ({ ...prev, [field]: value }));
        if (setLinkedDealFormData) {
            setLinkedDealFormData(prev => ({ ...prev, [field]: value }));
        }
    }, [setLinkedDealFormData]);

    const handleProductChange = useCallback((index, field, value) => {
        let formattedValue = value;
        if (["Buying_Price", "Selling_Price"].includes(field)) {
            formattedValue = value.replace(/[^0-9.]/g, "");
            if (formattedValue.includes(".")) {
                const [integerPart, decimalPart] = formattedValue.split(".");
                if (decimalPart.length > 3) {
                    formattedValue = parseFloat(`${integerPart}.${decimalPart}`).toFixed(3);
                } else {
                    formattedValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
                }
            }
            if (formattedValue !== "") formattedValue = parseFloat(formattedValue);
        }
        setOverriddenData(prev => {
            const updatedProducts = [...prev.products];
            updatedProducts[index] = { ...updatedProducts[index], [field]: formattedValue };
            if (setLinkedDealFormData) setLinkedDealFormData(p => ({ ...p, products: updatedProducts }));
            return { ...prev, products: updatedProducts };
        });
    }, [setLinkedDealFormData]);

    const handleAccordionChange = (index) => (event, isExpanded) => {
        setExpandedProduct(isExpanded ? index : null);
    };

    const handleSubmit = async () => {
        const buyingValid = buyingPTRef.current?.isValid() ?? true;
        const sellingValid = sellingPTRef.current?.isValid() ?? true;
        if (!buyingValid || !sellingValid) return;

        const linkedPfiId = linkedDealFormData?.Deal_PFI_Id;

        setIsSaving(true);
        try {
            if (linkedPfiId) {
                await Promise.all([
                    buyingPTRef.current?.handleSubmit(),
                    sellingPTRef.current?.handleSubmit(),
                ]);
            }
            onSubmit(overriddenData);
        } catch (error) {
            console.error("Error saving linked PFI payment terms:", error);
            handleApiError(error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { minHeight: '60vh', maxHeight: '90vh' } }}
        >
            <Typography
                variant="subtitle1"
                sx={{
                    fontSize: '14px',
                    padding: '5px 14px',
                    textAlign: 'center',
                    backgroundColor: '#0C969C',
                    color: '#fff',
                    fontWeight: 'bold',
                    borderBottom: '1px solid #ddd',
                }}
            >
                Manual save required for the following fields in the linked deal: {selectedLinkedDeal?.value}
            </Typography>

            <DialogContent dividers sx={{ p: 3 }}>
                <Grid container spacing={"10px"} className="align-items-start autocomplete">

                    <Grid size={{ xs: 6 }}>
                        <VirtualizedAutocomplete
                            isLoading={isLoadingCounterParties}
                            isMultiple={false} isObject={true} isWritable={isWritable} isRequired={true}
                            isLocked={Boolean(isPeriodLocked && isFieldLocked("Buyer_Id", "Deal Header", "Pre-Shipment", lockedFields))}
                            filterOn="Customer Name" options={customerCounterParties}
                            selectedOptions={overriddenData.Buyer_Id || null}
                            handleSelectChange={(_, newValue) => handleChange("Buyer_Id", newValue)}
                        />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <VirtualizedAutocomplete
                            isLoading={isLoadingCounterParties}
                            isMultiple={false} isObject={true} isWritable={isWritable} isRequired={true}
                            isLocked={Boolean(isPeriodLocked && isFieldLocked("Seller_Id", "Deal Header", "Pre-Shipment", lockedFields))}
                            filterOn="Supplier Name" options={supplierCounterParties}
                            selectedOptions={overriddenData.Seller_Id || null}
                            handleSelectChange={(_, newValue) => handleChange("Seller_Id", newValue)}
                        />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <VirtualizedAutocomplete
                            isLoading={isListDataLoading}
                            isMultiple={false} isObject={true} isWritable={isWritable} isRequired={true}
                            isLocked={Boolean(isPeriodLocked && isFieldLocked("Buying_Payment_Terms_Id", "Deal Header", "Pre-Shipment", lockedFields))}
                            filterOn="Buying Payment Terms *" options={paymentTerms}
                            selectedOptions={overriddenData.Buying_Payment_Terms_Id || null}
                            handleSelectChange={(_, newValue) => handleChange("Buying_Payment_Terms_Id", newValue)}
                        />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <VirtualizedAutocomplete
                            isLoading={isListDataLoading}
                            isMultiple={false} isObject={true} isWritable={isWritable} isRequired={true}
                            isLocked={Boolean(isPeriodLocked && isFieldLocked("Selling_Payment_Terms_Id", "Deal Header", "Pre-Shipment", lockedFields))}
                            filterOn="Selling Payment Terms *" options={paymentTerms}
                            selectedOptions={overriddenData.Selling_Payment_Terms_Id || null}
                            handleSelectChange={(_, newValue) => handleChange("Selling_Payment_Terms_Id", newValue)}
                        />
                    </Grid>

                    {overriddenData?.Buying_Tenor_Id?.id && (
                        <Grid size={{ xs: 6 }}>
                            <VirtualizedAutocomplete
                                isLoading={isListDataLoading}
                                isMultiple={false} isObject={true} isWritable={isWritable}
                                isLocked={Boolean(isPeriodLocked && isFieldLocked("Buying_Tenor_Id", "Deal Header", "Pre-Shipment", lockedFields))}
                                filterOn={`Buying Tenor ${overriddenData?.Buying_Payment_Terms_Id?.value === "LC" ? "*" : ""}`}
                                options={tenors} selectedOptions={overriddenData?.Buying_Tenor_Id || null}
                                handleSelectChange={(_, newValue) => handleChange("Buying_Tenor_Id", newValue)}
                            />
                        </Grid>
                    )}

                    {overriddenData?.Selling_Tenor_Id?.id && (
                        <Grid size={{ xs: 6 }}>
                            <VirtualizedAutocomplete
                                isLoading={isListDataLoading}
                                isMultiple={false} isObject={true} isWritable={isWritable}
                                isLocked={Boolean(isPeriodLocked && isFieldLocked("Selling_Tenor_Id", "Deal Header", "Pre-Shipment", lockedFields))}
                                filterOn={`Selling Tenor ${overriddenData?.Selling_Payment_Terms_Id?.value === "LC" ? "*" : ""}`}
                                options={tenors} selectedOptions={overriddenData?.Selling_Tenor_Id || null}
                                handleSelectChange={(_, newValue) => handleChange("Selling_Tenor_Id", newValue)}
                            />
                        </Grid>
                    )}

                </Grid>

                {/* ── Product Prices ── */}
                <Divider sx={{ my: 1 }} />
                <Accordion
                    sx={{ marginRight: "0.1em !important", marginLeft: "0 !important" }}
                    expanded={expandedFormSections.includes(5)}
                    onChange={(_, isExpanded) =>
                        setExpandedFormSections(
                            isExpanded
                                ? [...expandedFormSections, 5]
                                : expandedFormSections.filter(id => id !== 5)
                        )
                    }
                >
                    <AccordionSummary
                        id="linked-product-prices-header"
                        aria-controls="linked-product-prices-content"
                        expandIcon={<ExpandMoreIcon />}
                        slot="div"
                    >
                        <small className="page-heading title">Product Prices</small>
                    </AccordionSummary>

                    <AccordionDetails>
                        {!overriddenData.products || overriddenData.products.length === 0 ? (
                            <Typography variant="body2" sx={{ textAlign: 'center', padding: '16px', color: '#777', fontStyle: 'italic' }}>
                                There are no products. Please add products first.
                            </Typography>
                        ) : (
                            overriddenData.products.map((product, index) => {
                                const productValue = product?.Product_Id?.value || `Product ${index + 1}`;
                                const productIdValue = getProductIdValue(product);
                                const productKey = productIdValue
                                    ? `product-${productIdValue}`
                                    : `product-${product?.Product_Id?.id || index}-${index}`;

                                return (
                                    <Accordion key={productKey} expanded={expandedProduct === index} onChange={handleAccordionChange(index)} sx={{ mb: 1 }}>
                                        <AccordionSummary id={`product-header-${index}`} aria-controls={`product-content-${index}`} expandIcon={<ExpandMoreIcon />} slot="div">
                                            <small className="page-heading fw-bold item">{productValue}</small>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={"10px"}>
                                                <Grid size={{ xs: 6 }} className="form-text-field">
                                                    <TextField label="Product" size="small" disabled value={product?.Product_Id?.value || ""} slotProps={{ inputLabel: { shrink: Boolean(product?.Product_Id?.value) } }} />
                                                </Grid>
                                                <Grid size={{ xs: 6 }} className="form-text-field">
                                                    <TextField
                                                        label="Unit of Measurement" size="small" disabled
                                                        value={product?.Product_Id ? productData?.find((_product) => _product?.id === product?.Product_Id?.id)?.Unit_of_Measurement || "" : ""}
                                                        slotProps={{ inputLabel: { shrink: product?.Product_Id && productData?.some((_product) => _product.id === product.Product_Id.id && _product.Unit_of_Measurement) } }}
                                                    />
                                                </Grid>
                                                {product?.Product_Grade_Id && (
                                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                                        <TextField label="Product Grade" size="small" disabled value={product?.Product_Grade_Id?.value || ""} slotProps={{ inputLabel: { shrink: Boolean(product?.Product_Grade_Id?.value) } }} />
                                                    </Grid>
                                                )}
                                                <Grid size={{ xs: 6 }} className="form-text-field">
                                                    <TextField label="Quantity" size="small" disabled value={product?.Quantity || ""} slotProps={{ inputLabel: { shrink: Boolean(product?.Quantity) } }} />
                                                </Grid>
                                                <Grid size={{ xs: 6 }} className="form-text-field">
                                                    <TextField
                                                        label="Selling Price" type="number" size="small" required
                                                        disabled={Boolean(isPeriodLocked && isFieldLocked("Selling_Price", "Deal Header", "Pre-Shipment", lockedFields))}
                                                        value={product.Selling_Price ?? ""}
                                                        onChange={(e) => handleProductChange(index, "Selling_Price", e.target.value)}
                                                        slotProps={{
                                                            input: {
                                                                readOnly: !isWritable, min: 0, max: 2147483647, step: "0.001",
                                                                endAdornment: (
                                                                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                                                        {Boolean(isPeriodLocked && isFieldLocked("Selling_Price", "Deal Header", "Pre-Shipment", lockedFields)) && (
                                                                            <Tooltip title="This field is locked and cannot be edited due to period lock." arrow placement="top">
                                                                                <InfoIcon fontSize="small" sx={{ cursor: 'default' }} />
                                                                            </Tooltip>
                                                                        )}
                                                                    </InputAdornment>
                                                                ),
                                                            },
                                                            inputLabel: { shrink: Boolean(product.Selling_Price !== '' && product.Selling_Price !== null && product.Selling_Price !== undefined) }
                                                        }}
                                                        onKeyDown={(e) => { if (["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(e.key)) e.preventDefault(); }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 6 }} className="form-text-field">
                                                    <TextField
                                                        label="Buying Price" type="number" size="small" required
                                                        disabled={Boolean(isPeriodLocked && isFieldLocked("Buying_Price", "Deal Header", "Pre-Shipment", lockedFields))}
                                                        value={product.Buying_Price ?? ""}
                                                        onChange={(e) => handleProductChange(index, "Buying_Price", e.target.value)}
                                                        slotProps={{
                                                            input: {
                                                                readOnly: !isWritable, min: 0, max: 2147483647, step: "0.001",
                                                                endAdornment: (
                                                                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                                                        {Boolean(isPeriodLocked && isFieldLocked("Buying_Price", "Deal Header", "Pre-Shipment", lockedFields)) && (
                                                                            <Tooltip title="This field is locked and cannot be edited due to period lock." arrow placement="top">
                                                                                <InfoIcon fontSize="small" sx={{ cursor: 'default' }} />
                                                                            </Tooltip>
                                                                        )}
                                                                    </InputAdornment>
                                                                ),
                                                            },
                                                            inputLabel: { shrink: Boolean(product.Buying_Price !== '' && product.Buying_Price !== null && product.Buying_Price !== undefined) }
                                                        }}
                                                        onKeyDown={(e) => { if (["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(e.key)) e.preventDefault(); }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 6 }} className="form-text-field">
                                                    <TextField label="HS Code" size="small" disabled value={product?.HS_Code || ""} slotProps={{ inputLabel: { shrink: Boolean(product?.HS_Code) } }} />
                                                </Grid>
                                                {product?.Product_Specification && (
                                                    <Grid size={{ xs: 12 }} className="form-text-field">
                                                        <TextField label="Specification" size="small" disabled multiline rows={3} value={product?.Product_Specification || ""} slotProps={{ inputLabel: { shrink: Boolean(product?.Product_Specification) } }} />
                                                    </Grid>
                                                )}
                                                <Grid size={{ xs: 12 }} className="form-text-field">
                                                    <TextField label="Description" size="small" disabled multiline rows={3} value={product?.Product_Description || ""} slotProps={{ inputLabel: { shrink: Boolean(product?.Product_Description) } }} />
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* ── Buying Payment Terms ── */}
                <LinkedBuyingPaymentTerms
                    ref={buyingPTRef}
                    isWritable={isWritable}
                    isListDataLoading={isListDataLoading}
                    paymentTermTypes={paymentTermTypes}
                    selectedPFI={linkedDealFormData?.Deal_PFI_Id ?? null}
                    selectedBusinessUnit={selectedBusinessUnit}
                    isPeriodLocked={isPeriodLocked}
                    lockedFields={lockedFields}
                    dealHeaderFormData={overriddenData}
                    dealValue={linkedBuyingDealValue}
                    expandedFormSections={expandedFormSections}
                    setExpandedFormSections={setExpandedFormSections}
                    setShowAlert={setShowAlert}
                    setAlertMessage={setAlertMessage}
                    setAlertType={setAlertType}
                    logOut={logOut}
                />

                {/* ── Selling Payment Terms ── */}
                <LinkedSellingPaymentTerms
                    ref={sellingPTRef}
                    isWritable={isWritable}
                    isListDataLoading={isListDataLoading}
                    paymentTermTypes={paymentTermTypes}
                    selectedPFI={linkedDealFormData?.Deal_PFI_Id ?? null}
                    selectedBusinessUnit={selectedBusinessUnit}
                    isPeriodLocked={isPeriodLocked}
                    lockedFields={lockedFields}
                    dealHeaderFormData={overriddenData}
                    dealValue={linkedSellingDealValue}
                    expandedFormSections={expandedFormSections}
                    setExpandedFormSections={setExpandedFormSections}
                    setShowAlert={setShowAlert}
                    setAlertMessage={setAlertMessage}
                    setAlertType={setAlertType}
                    logOut={logOut}
                />
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" size="medium" disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="medium"
                    disabled={isSaving}
                    sx={{ bgcolor: '#0C969C', '&:hover': { bgcolor: '#0a7a7f' } }}
                >
                    {isSaving ? "Saving..." : "Apply & Continue"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LinkedDealOverridePopup;